import { sanitizeQuery } from '../../../../helpers/sanitizeQueryHelper'
import { AppConfigurationKey } from '../../../../model/enums/appConfigurationKey'
import { ObjectionTableModel } from '../../../../pages/invoicesPage/invoicesPage.Model'
import {
  AdjustmentConceptSearchRequestModel,
  ClosedSettlementsSearchRequest,
  RegenerateFormData,
  SettlementSearchRequestModel,
  SettlementsStateValidationRequestModel
} from '../../../../pages/settlements/settlements.models'
import { getAppConfigurationValue } from '../../../../redux'
import AxiosInstance from '../../../http-config'
import {
  AdjustmentConceptSearchResponseModel,
  SettlementsCancelledCheckModel,
  SettlementSearchResponseModel,
  SettlementsInvoicesKPIsResponseModel,
  SettlementsKPIsResponseModel,
  SettlementsObjectionsTableModel,
  SettlementsStateValidationResponseModel,
  SettlementsStatusKPIsResponseModel,
  SettlementTableModel
} from './model/settlements'

export const apiGetSettlementsLandingKpis = async (
  executionCycleId: number
): Promise<SettlementsKPIsResponseModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Settlements/GetSettlementsKpis(${executionCycleId})`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiGetSettlementsStatusKpis = async (
  executionCycleId: number
): Promise<SettlementsStatusKPIsResponseModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Settlements/GetSettlementsStatusKpis(${executionCycleId})`
  ).then(response => response.data)
}

export const apiGetSettlementsInvoicesLandingKpis = async (
  executionCycleId: number
): Promise<SettlementsInvoicesKPIsResponseModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Settlements/GetSettlementsInvoicesKpis(${executionCycleId})`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiGetSettlementsObjections = async (
  executionCycleId: number
): Promise<SettlementsObjectionsTableModel[] | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Settlements/viewObjections(${executionCycleId})`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiFetchObjectionsBySettlementID = async (
  settlementID: number
): Promise<ObjectionTableModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(`${apiSuffixUrl}/api/Settlements/${settlementID}/Objections`).then(
    response => {
      if (response.data.length > 0) {
        return response.data
      } else {
        return []
      }
    }
  )
}

export const apiGetSettlementsTableData = async (
  executionCycleId: number,
  objCode: string | null,
  invoiceCycleType: number,
  subjectType: number,
  billingType: number
): Promise<SettlementTableModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const params = {
    executionCycleId,
    objCode,
    invoiceCycleType,
    subjectType,
    billingType
  }
  return await AxiosInstance.get(`${apiSuffixUrl}/api/Settlements/GetSettlementsByObjectionCode`, {
    params
  }).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiPostClosedSettlementsSearch = async (
  request: ClosedSettlementsSearchRequest
): Promise<SettlementTableModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/Settlements/ClosedByEmissionDate`,
    request
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return []
    }
  })
}

export const apiPostGenerateLiquidations = async (
  executionCycleId: number,
  params?: RegenerateFormData
): Promise<any> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.post(`${apiSuffixUrl}/api/Settlements/GenerateLiquidations`, {
    cycleExecutionId: executionCycleId,
    ...params
  }).then(response => {
    if (response.data) {
      return response.data
    }
  })
}

export const apiPostSettlementsSearch = async (
  request: SettlementSearchRequestModel
): Promise<SettlementSearchResponseModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  // Sanitize dynamic search fields to prevent SQL injection (single point of defense)
  const sanitizedRequest = { ...request }

  // Sanitize whichever field is being used based on search type
  const fieldsToSanitize: (keyof SettlementSearchRequestModel)[] = [
    'contractCode',
    'cupsCode',
    'subjectName',
    'tradeCode',
    'productCode',
    'invoiceCode'
  ]

  fieldsToSanitize.forEach(field => {
    const value = sanitizedRequest[field]
    if (value && typeof value === 'string') {
      ;(sanitizedRequest as any)[field] = sanitizeQuery(value, [' ', '-', '_'])
    }
  })

  return await AxiosInstance.post(`${apiSuffixUrl}/api/Settlements/Search`, sanitizedRequest).then(
    response => {
      if (response.data) {
        return response.data
      }
    }
  )
}

export const apiPostAdjustmentConceptsSearch = async (
  request: AdjustmentConceptSearchRequestModel
): Promise<AdjustmentConceptSearchResponseModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  // Sanitizar campos de búsqueda dinámica para prevenir inyección SQL
  const sanitizedRequest = { ...request }
  const fieldsToSanitize: (keyof AdjustmentConceptSearchRequestModel)[] = [
    'contractCode',
    'cupsCode',
    'invoiceCode',
    'conceptCode',
    'settlementGroupCode'
  ]
  fieldsToSanitize.forEach(field => {
    const value = sanitizedRequest[field]
    if (value && typeof value === 'string') {
      ;(sanitizedRequest as any)[field] = sanitizeQuery(value, [' ', '-', '_'])
    }
  })

  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/Settlements/SearchAdjustmentExecution`,
    sanitizedRequest
  ).then(response => {
    if (response.data) {
      return response.data
    }
  })
}

export const apiPostValidateSettlementsState = async (
  request: SettlementsStateValidationRequestModel
): Promise<SettlementsStateValidationResponseModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.post(`${apiSuffixUrl}/api/Settlements/ValidateState`, request).then(
    response => {
      if (response.data) {
        return response.data
      }
    }
  )
}

export const apiGetHasCancelledOrPendingSettlements = async (
  executionCycleId: number
): Promise<SettlementsCancelledCheckModel | undefined> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/Settlements/HasCancelledOrPending(${executionCycleId})`
  ).then(response => {
    if (response.data) {
      return response.data
    } else {
      return undefined
    }
  })
}
